import type { Session, Message, KnowledgePromptState, AssistantPart, AssistantArtifactPart, AssistantSubagentPart, AssistantMediaPart } from "./types";
import { refresh } from "@/auth/refresh";
import { readExportFormats, readTablePreview } from "./artifactFields";
import { friendlyError } from "@/api/friendlyError";

export interface ChatClientOptions {
  apiUrl: string;
  fetch?: typeof globalThis.fetch;
  /** Called once on a 401 to attempt a silent session refresh before the
   *  request is retried. Defaults to the BFF /auth/refresh. Override in tests. */
  onUnauthorized?: () => Promise<boolean>;
  /** Extra headers on every request. The embed widget sends `X-Embed-Key`
   *  here instead of relying on same-origin cookies. Empty for the SPA. */
  extraHeaders?: Record<string, string>;
  /** Resolves the customer end-user JWT for embed mode; sent as
   *  `Authorization: Bearer <token>` on every request (re-invoked on 401 so the
   *  host can refresh it). Omit for the cookie/BFF SPA path. */
  getAuthToken?: () => string | Promise<string>;
  /** Chat POST path. SPA = "/v1/chat" (default); embed = "/embed/v1/chat". */
  chatPath?: string;
  /** fetch credentials mode. SPA = "include" (cookies, default); embed =
   *  "omit" (bearer-only, cross-origin, no CSRF surface). */
  credentials?: RequestCredentials;
}

export interface OpenChatStreamArgs {
  text: string;
  session_id?: string;
  message_id?: string;
  model?: string;
  /** Comma-separated list. Intentionally NOT wired into any UI today
   *  — the backend activates every bundled module per session by
   *  default (server.py:resolve_session_modules), and the LLM uses
   *  activate_module to scope per turn. This field is kept in the
   *  request type so a future per-session override picker can plug
   *  in without changing the wire contract. See Slice 4 of
   *  docs/superpowers/plans/2026-06-04-chat-ui-backend-gap.md. */
  modules?: string;
  payload?: Record<string, unknown>;
  /** Document ids attached to this turn. Sent as payload.attachment_ids
   *  (the backend reads body.payload.attachment_ids). */
  attachment_ids?: string[];
  /** Edit a prior user message and branch off it. text becomes the
   *  replacement content. Mutually exclusive with `regenerate`. */
  edit_message_id?: string;
  /** Re-run the last assistant reply on the current branch. text may
   *  be omitted — the server reuses the last user message. */
  regenerate?: boolean;
  /** Project to associate on session creation. Only honoured by the
   *  backend on the very first message (no rows yet), so safe to always
   *  send — the server ignores it once the session already exists. */
  project_id?: string | null;
}

export interface ToolDescriptor {
  name: string;
  description: string;
}

export interface ModelDescriptor {
  id: string;
  label: string;
  is_default: boolean;
  is_auto: boolean;
  provider?: string;
}

export interface ModelCatalog {
  models: ModelDescriptor[];
  default: string;
  router_enabled: boolean;
}

/** GET /v1/config — backend feature flags. */
export interface BackendConfig {
  features: {
    plan_mode: boolean;
    background_subagents: boolean;
  };
}

export class ChatClient {
  private apiUrl: string;
  private fetchImpl: typeof globalThis.fetch;
  private onUnauthorized: () => Promise<boolean>;
  private extraHeaders: Record<string, string>;
  private getAuthToken?: () => string | Promise<string>;
  private chatPath: string;
  private credentials: RequestCredentials;

  constructor(opts: ChatClientOptions) {
    this.apiUrl = opts.apiUrl.replace(/\/+$/, "");
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = opts.onUnauthorized ?? refresh;
    this.extraHeaders = opts.extraHeaders ?? {};
    this.getAuthToken = opts.getAuthToken;
    this.chatPath = opts.chatPath ?? "/v1/chat";
    this.credentials = opts.credentials ?? "include";
  }

  /** Resolve the per-request auth/identity headers. In embed mode this is
   *  X-Embed-Key (via extraHeaders) + Authorization: Bearer <customer jwt>
   *  (via getAuthToken); in SPA mode it's empty and cookies carry the session. */
  private async authHeaders(): Promise<Record<string, string>> {
    const h: Record<string, string> = { ...this.extraHeaders };
    if (this.getAuthToken) {
      const tok = await this.getAuthToken();
      if (tok) h["Authorization"] = `Bearer ${tok}`;
    }
    return h;
  }

  /** Merge credentials mode + extra/auth headers onto a RequestInit. */
  private async withAuth(init?: RequestInit): Promise<RequestInit> {
    return {
      ...init,
      credentials: this.credentials,
      headers: { ...(init?.headers ?? {}), ...(await this.authHeaders()) },
    };
  }

  /** Authed fetch with a single silent refresh-and-retry on 401 (BFF cookie
   *  refresh for the SPA, or getAuthToken re-fetch for embed). */
  private async authedFetch(path: string, init?: RequestInit): Promise<Response> {
    const url = `${this.apiUrl}${path}`;
    let resp = await this.fetchImpl(url, await this.withAuth(init));
    if (resp.status === 401 && (await this.onUnauthorized())) {
      resp = await this.fetchImpl(url, await this.withAuth(init));
    }
    if (!resp.ok) {
      throw new Error(friendlyError(resp.status));
    }
    return resp;
  }

  async listSessions(opts?: { limit?: number; offset?: number }): Promise<Session[]> {
    // Paged: the sidebar loads a window and asks for more on demand — loading
    // every session up front made org switches/session lists crawl once
    // accounts accumulated history.
    const q = new URLSearchParams();
    if (opts?.limit != null) q.set("limit", String(opts.limit));
    if (opts?.offset != null) q.set("offset", String(opts.offset));
    const qs = q.toString();
    const resp = await this.authedFetch(`/core/v1/sessions${qs ? `?${qs}` : ""}`);
    const body = (await resp.json()) as {
      sessions: Array<{
        session_id: string;
        title: string;
        created_at: string;
        last_active?: string;
        message_count?: number;
        project_id?: string | null;
      }>;
    };
    return (body.sessions ?? []).map((s) => ({
      id: s.session_id,
      title: s.title,
      created_at: s.created_at,
      updated_at: s.last_active ?? s.created_at,
      project_id: s.project_id ?? null,
    }));
  }

  async getMessages(sessionId: string, opts?: { limit?: number }): Promise<Message[]> {
    // limit = newest-N tail (chronological). Session switches load a tail
    // instead of the whole history; "load earlier" refetches without a limit.
    const qs = opts?.limit != null ? `?limit=${opts.limit}` : "";
    const resp = await this.authedFetch(`/core/v1/sessions/${sessionId}/messages${qs}`);
    const body = (await resp.json()) as {
      session_id: string;
      messages: ApiMessage[];
    };
    return (body.messages ?? []).map((m, i) => mapApiMessageToMessage(m, sessionId, i));
  }

  async renameSession(sessionId: string, title: string): Promise<void> {
    await this.authedFetch(`/core/v1/sessions/${sessionId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
  }

  /** Set or clear a thumbs feedback vote on an assistant message. Passing
   *  vote=null clears the existing vote. */
  async sendMessageFeedback(
    sessionId: string,
    messageId: string,
    vote: "up" | "down" | null,
  ): Promise<void> {
    await this.authedFetch(
      `/core/v1/sessions/${sessionId}/messages/${messageId}/feedback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vote }),
      },
    );
  }

  /** Record the user's answer to the "keep as knowledge" card on a
   *  web-backed answer, so it does not come back on reload (spec §3.3). */
  async setKnowledgePrompt(
    sessionId: string,
    messageId: string,
    state: KnowledgePromptState,
  ): Promise<void> {
    await this.authedFetch(
      `/core/v1/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/knowledge-prompt`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state }),
      },
    );
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.authedFetch(`/core/v1/sessions/${sessionId}`, { method: "DELETE" });
  }

  async openChatStream(args: OpenChatStreamArgs, signal?: AbortSignal): Promise<Response> {
    const url = `${this.apiUrl}${this.chatPath}`;
    const { attachment_ids, payload, ...rest } = args;
    const mergedPayload =
      attachment_ids && attachment_ids.length
        ? { ...(payload ?? {}), attachment_ids }
        : payload;
    const body = { ...rest, ...(mergedPayload ? { payload: mergedPayload } : {}) };
    const doFetch = async () =>
      this.fetchImpl(url, {
        method: "POST",
        credentials: this.credentials,
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(await this.authHeaders()),
        },
        body: JSON.stringify(body),
        signal,
      });
    let resp = await doFetch();
    // Embed/SPA parity: one silent refresh-and-retry on 401.
    if (resp.status === 401 && (await this.onUnauthorized())) {
      resp = await doFetch();
    }
    if (!resp.ok || !resp.body) {
      // Surface the backend's `detail` (e.g. "no prior user message to
      // regenerate from", "edit_message_id not found in this session") so the
      // user/dev sees the actual cause instead of a bare status. Reading the
      // body is best-effort — fall back to the status line if it isn't JSON.
      let detail = "";
      try {
        const text = await resp.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            detail = typeof parsed?.detail === "string" ? parsed.detail : "";
          } catch {
            /* non-JSON body — ignore, use the generic message */
          }
        }
      } catch {
        /* body already consumed or unreadable — ignore */
      }
      throw new Error(friendlyError(resp.status, detail));
    }
    return resp;
  }

  async cancel(messageId: string): Promise<void> {
    const url = `${this.apiUrl}${this.chatPath}/${encodeURIComponent(messageId)}/cancel`;
    await this.fetchImpl(url, await this.withAuth({ method: "POST" }));
  }

  async steer(sessionId: string, text: string): Promise<void> {
    const url = `${this.apiUrl}${this.chatPath}/${encodeURIComponent(sessionId)}/steer`;
    const resp = await this.fetchImpl(
      url,
      await this.withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      }),
    );
    if (!resp.ok) {
      throw new Error(friendlyError(resp.status));
    }
  }

  /** POST a structured decision to /steer through the standard auth path
   *  (chatPath-aware URL, embed/SPA headers, 401 refresh-and-retry). The
   *  decision POSTs used to hardcode /v1/chat + credentials:"include" with no
   *  auth headers, so every embed Continue/Approve 401'd as "session expired". */
  private async steerDecision(sessionId: string, decision: Record<string, unknown>): Promise<void> {
    await this.authedFetch(
      `${this.chatPath}/${encodeURIComponent(sessionId)}/steer`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      },
    );
  }

  /** Submit a blocking-plan-mode decision to /steer. The backend stores it at
   *  plan_decision:{sid}; the next turn's resume applies it (approve/edit →
   *  execute, reject → abandon). edited_todos is sent only for "edit". */
  async planDecision(
    sessionId: string,
    decision: "approve" | "edit" | "reject",
    opts?: { edited_todos?: unknown[]; reason?: string },
  ): Promise<void> {
    await this.steerDecision(sessionId, {
      type: "plan_decision",
      decision,
      ...(opts?.edited_todos ? { edited_todos: opts.edited_todos } : {}),
      ...(opts?.reason ? { reason: opts.reason } : {}),
    });
  }

  /** Submit a budget_decision to /steer — resumes (or stops) a turn paused at
   *  its cost/time/iteration cap (budget_paused). */
  async budgetDecision(sessionId: string, decision: "continue" | "stop"): Promise<void> {
    await this.steerDecision(sessionId, { type: "budget_decision", decision });
  }

  /** Answer a `review_required` (per-tool approval gate) on /steer.
   *
   *  THIS DOES NOT RESUME ANYTHING. The backend only parks the verdict at
   *  `tool_decision:{session_id}`; `resume_pending_if_decided` consumes it at
   *  the top of the NEXT turn, before the loop runs. There is no resume
   *  endpoint — the caller must start a turn for the approved tool to execute.
   *
   *  `callId` is echoed from the frame. Shape is FLAT and snake_case, matching
   *  `_SteerBody.decision` exactly; `steerDecision` wraps it as `{decision: …}`.
   *  A body shaped any other way is stored and then silently ignored by the
   *  resume path, leaving the turn paused forever with no error anywhere.
   *
   *  APPROVE AND REJECT ONLY — `edit` is deliberately absent, not missing.
   *  `approval_gate.effective_args` takes `edited_args` wholesale with no
   *  schema or identity check, and `_approval_resume` never compares the
   *  decision's `call_id` to the pending one, so an edited send could execute
   *  arguments the card never displayed. Spec §5.3: dropping `edit` is the only
   *  way "the card shows exactly what will be sent" holds. Do not add it here
   *  without first making the decision echo a hash of the displayed args. */
  async toolDecision(
    sessionId: string,
    callId: string,
    decision: "approve" | "reject",
    opts?: { reason?: string },
  ): Promise<void> {
    await this.steerDecision(sessionId, {
      type: "tool_decision",
      call_id: callId,
      decision,
      ...(decision === "reject" && opts?.reason ? { reason: opts.reason } : {}),
    });
  }

  /** GET /v1/tools — the assistant's tool catalog for the caller's tenant.
   *  Returns an empty list on auth/error so the composer can degrade
   *  gracefully (chips just don't render). */
  async listTools(): Promise<ToolDescriptor[]> {
    try {
      const resp = await this.authedFetch("/v1/tools");
      const body = (await resp.json()) as {
        tools: Array<{ name: string; description: string }>;
      };
      return body.tools ?? [];
    } catch {
      return [];
    }
  }

  /** GET /v1/commands — built-in slash-command registry, used to seed the
   *  composer's slash picker before the first turn (the SSE
   *  `available_commands` frame only arrives once a turn starts). Returns
   *  an empty list on error so the picker degrades to plugin-only entries
   *  from the SSE frame. */
  async listCommands(): Promise<
    Array<{
      plugin: string;
      name: string;
      namespaced_name: string;
      title: string;
      description: string;
      prompt_template: string;
    }>
  > {
    try {
      const resp = await this.authedFetch("/v1/commands");
      const body = (await resp.json()) as {
        commands: Array<{
          plugin: string;
          name: string;
          namespaced_name: string;
          title: string;
          description: string;
          prompt_template: string;
        }>;
      };
      return body.commands ?? [];
    } catch {
      return [];
    }
  }

  /** GET /v1/models — model picker payload (auto + allowlist + default).
   *  Returns null on auth/error so the picker can fail silent (hides
   *  itself) rather than block the composer. */
  async listModels(): Promise<ModelCatalog | null> {
    try {
      const resp = await this.authedFetch("/v1/models");
      return (await resp.json()) as ModelCatalog;
    } catch {
      return null;
    }
  }

  /** GET /v1/config — backend feature flags so the UI only offers controls the
   *  backend honors (e.g. the Plan toggle). Returns null on error so the UI
   *  falls back to its defaults. */
  async getConfig(): Promise<BackendConfig | null> {
    try {
      const resp = await this.authedFetch("/v1/config");
      return (await resp.json()) as BackendConfig;
    } catch {
      return null;
    }
  }

  /** GET /v1/sessions/{id}/export?format=md|json — fetches the raw
   *  exported body so the caller can trigger a browser download. */
  async exportSession(
    sessionId: string,
    format: "md" | "json" = "md",
  ): Promise<{ filename: string; body: string; mime: string }> {
    const resp = await this.authedFetch(
      `/core/v1/sessions/${encodeURIComponent(sessionId)}/export?format=${format}`,
    );
    const body = await resp.text();
    // Prefer the server's suggested filename from Content-Disposition;
    // fall back to a sane default if the header is missing (e.g. behind
    // a proxy that strips it).
    const cd = resp.headers.get("content-disposition") ?? "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m?.[1] ?? `chat.${format}`;
    const mime =
      resp.headers.get("content-type") ??
      (format === "json" ? "application/json" : "text/markdown");
    return { filename, body, mime };
  }
}

/** Shape of a single message as returned by GET /core/v1/sessions/{id}/messages. */
interface ApiMessage {
  id?: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string | Array<{ text?: string; type?: string }>;
  agent_id?: string;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

/**
 * Map a single API message to the `Message` shape the UI renders, rehydrating
 * per-message display state the backend persisted into metadata so attachment
 * chips, the tool-call trail ("Worked for Ns"), the work timeline, and the
 * metrics footer survive a reload instead of being live-only.
 *
 * `index` is only used to synthesize an id for older rows that predate the
 * server-supplied `id` column.
 */
export function mapApiMessageToMessage(m: ApiMessage, sessionId: string, index = 0): Message {
  const meta = (m.metadata ?? {}) as Record<string, unknown>;
  const metrics = meta.metrics as Message["metrics"] | undefined;
  const toolCalls = meta.toolCalls as Message["toolCalls"] | undefined;
  const attachments = meta.attachments as Message["attachments"] | undefined;
  // metadata.feedback = { vote: "up"|"down", created_at } when the user has
  // rated this message; surface just the vote for the thumbs toggle.
  const feedbackMeta = meta.feedback as { vote?: "up" | "down" } | undefined;
  const feedback = feedbackMeta?.vote ?? null;
  const thinking = meta.thinking as string | undefined;
  const thinkingMs = meta.thinkingMs as number | undefined;
  // metadata.workLog is the chronological timeline (tool calls + thinking
  // spans) persisted by the backend so a reload/scroll-back shows the same
  // WorkTimeline the user saw live.
  const workLog = meta.workLog as Message["workLog"] | undefined;
  // metadata.knowledge_prompt = the user's answer to the "keep as knowledge"
  // card; present means the card must not come back.
  const knowledgePrompt = meta.knowledge_prompt as KnowledgePromptState | undefined;
  return {
    // Prefer the server-supplied id (DB row UUID) so edit-and-regenerate
    // can target it; fall back to a positional synthetic id for older
    // rows where the column wasn't populated.
    id: m.id ?? `${sessionId}-${index}`,
    session_id: sessionId,
    role: m.role,
    content: normalizeContent(m.content),
    created_at: m.timestamp ?? "",
    ...(attachments && attachments.length ? { attachments } : {}),
    ...(toolCalls && toolCalls.length ? { toolCalls } : {}),
    ...(metrics ? { metrics, durationMs: metrics.total_latency_ms } : {}),
    ...(feedback ? { feedback } : {}),
    ...(thinking ? { thinking } : {}),
    ...(thinkingMs != null ? { thinkingMs } : {}),
    ...(workLog && workLog.length ? { workLog } : {}),
    ...(knowledgePrompt ? { knowledgePrompt } : {}),
  };
}

/**
 * The backend returns assistant message content as an array of content
 * blocks (Anthropic-style: [{text: "..."}, {tool_use: ...}, ...]).
 * If all blocks are plain text, we flatten to a string for backward compat.
 * If any block is an artifact, we pass through as AssistantPart[] so that
 * inline artifacts survive a page reload (fixes #5).
 */
function normalizeContent(
  c: string | Array<Record<string, unknown>>,
): string | AssistantPart[] {
  if (typeof c === "string") return c;
  if (!Array.isArray(c)) return "";

  // Flatten to a string only when every block is plain text. A non-text block
  // (artifact, or a subagent card persisted from a background subagent's
  // answer) must be preserved as a parts array so the UI can rehydrate it.
  const hasNonText = c.some(
    (b) => b?.type === "artifact" || b?.type === "subagent" || b?.type === "media",
  );
  if (!hasNonText) {
    return c
      .map((b) => (typeof b?.text === "string" ? b.text : ""))
      .join("");
  }

  // Pass through as parts array. Each block must shape match an AssistantPart.
  return c.map((b): AssistantPart => {
    if (b?.type === "artifact") {
      return {
        type: "artifact",
        artifact_id: String(b.artifact_id ?? ""),
        artifact_kind: (b.kind ?? "file") as AssistantArtifactPart["artifact_kind"],
        title: (b.title ?? null) as string | null,
        mime: (b.mime ?? null) as string | null,
        language: (b.language ?? null) as string | null,
        text: String(b.text ?? ""),
        status: (b.status ?? "complete") as AssistantArtifactPart["status"],
        error: typeof b.error === "string" ? b.error : undefined,
        ...(b.display === "export_only" ? { display: "export_only" as const } : {}),
        ...(typeof b.size === "number" ? { size: b.size } : {}),
        ...readExportFormats(b.export_formats),
        ...readTablePreview(b.preview),
      };
    }
    if (b?.type === "subagent") {
      // Rehydrate a persisted background-subagent card (the worker wrote this
      // when the job completed after the parent turn ended). `events` is empty
      // on reload — the card renders the final status + result text.
      return {
        type: "subagent",
        job_id: String(b.job_id ?? ""),
        name: typeof b.name === "string" ? b.name : "Background task",
        status: (b.status ?? "succeeded") as AssistantSubagentPart["status"],
        tools: Array.isArray(b.tools) ? (b.tools as string[]) : [],
        current_event_kind:
          typeof b.current_event_kind === "string" ? b.current_event_kind : null,
        events: Array.isArray(b.events) ? (b.events as AssistantSubagentPart["events"]) : [],
        result: b.result ?? null,
        error: typeof b.error === "string" ? b.error : null,
      };
    }
    if (b?.type === "media") {
      // Rehydrate a persisted media part (generated image/audio/video). The
      // server inflates the stored media_ref into a full media block with a
      // freshly presigned url on load, so a reload surfaces the asset inline
      // instead of dropping it (fixes generated images vanishing on reopen).
      return {
        type: "media",
        artifact_id: String(b.artifact_id ?? ""),
        media_kind: (b.media_kind ?? b.kind ?? "image") as AssistantMediaPart["media_kind"],
        mime: typeof b.mime === "string" ? b.mime : "",
        url: typeof b.url === "string" ? b.url : "",
        alt: typeof b.alt === "string" ? b.alt : "",
        width: typeof b.width === "number" ? b.width : null,
        height: typeof b.height === "number" ? b.height : null,
        duration_ms: typeof b.duration_ms === "number" ? b.duration_ms : null,
        prompt: typeof b.prompt === "string" ? b.prompt : null,
        status: (b.status ?? "ready") as AssistantMediaPart["status"],
      };
    }
    return { type: "text", text: typeof b?.text === "string" ? b.text : "" };
  });
}

function withCreds(init: RequestInit | undefined): RequestInit {
  return {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.headers ?? {}),
    },
  };
}
